import type { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'
import { applyAliases, expandBaseInstructionsForOverride } from 'helpers/uiConfig.helper/uiConfig.helper'
import { getAliasesForAliasExpansion } from 'helpers/uiConfig.helper/uiConfigResolutionPipeline.helper'
import type { AppliedSettingSeries, OptJobRun } from './appliedSettingValues.types'
import { mergeDbsInstructionsOverUnits, withSubtypes } from './dbsInstructions.helper'

type SettingSeries = AppliedSettingSeries & { kind: 'setting'; property: ConfigurableProperty }

function isSettingSeries(series: AppliedSettingSeries): series is SettingSeries {
  return series.kind === 'setting'
}

/** At most one run per kind, the first given, so no return id is requested twice. */
function firstRunOfEachKind(runs: OptJobRun[]): OptJobRun[] {
  return runs.filter((run, index) => runs.findIndex((other) => other.kind === run.kind) === index)
}

/**
 * BaseConfig's dataset instructions for one opt-job run: its aliases applied, and its return ids prefixed so
 * outcome and optimal data do not collide.
 */
function expandForRun(baseConfig: UiConfig, run: OptJobRun): Partial<DatasetInstruction>[] {
  return expandBaseInstructionsForOverride(baseConfig.dataset_instructions, {
    alias: run.aliases,
    return_id_prefix: run.returnIdPrefix,
  })
}

/**
 * Resolves what the run aliases left as aliases, including a run alias that names another alias:
 * BaseConfig's `props.alias` under the caller's override aliases.
 */
function applyBaseConfigAliasesUnderOverride(
  baseConfig: UiConfig,
  instructions: Partial<DatasetInstruction>[],
  overrideAlias: UiConfigAliases
): Partial<DatasetInstruction>[] {
  const config: UiConfig = { component: baseConfig.component, alias: {}, props: {}, dataset_instructions: instructions }

  return applyAliases(config, getAliasesForAliasExpansion(baseConfig, overrideAlias)).dataset_instructions
}

/** BaseConfig's instructions for a run, with its subtypes set and DbS requests that differ only in units merged. */
function toRunInstructions(
  baseConfig: UiConfig,
  run: OptJobRun,
  overrideAlias: UiConfigAliases
): Partial<DatasetInstruction>[] {
  const instructions = applyBaseConfigAliasesUnderOverride(baseConfig, expandForRun(baseConfig, run), overrideAlias)

  return mergeDbsInstructionsOverUnits(instructions.map((instruction) => withSubtypes(instruction, run.subtypes ?? [])))
}

/** One request for the applied values of every setting series, falling back to default values. */
function toPropertyValuesInstruction(
  settings: SettingSeries[],
  priorityLevels: number[],
  systemId: number
): Partial<DigitalTwinPropertyValuesDatasetInstruction> {
  return {
    type: 'dt_property_values',
    return_id: 'applied_setting_values',
    filter: {},
    contract: {
      sources: settings.map(({ returnId, property }) => ({
        return_id: returnId,
        object_name: property.name,
        property_name: property.attribute,
        setting_level: property.level,
      })),
      system_id: String(systemId),
      priority_levels: priorityLevels,
      use_default_values: true,
    },
  }
}

/**
 * Builds the dataset instructions that fetch the data behind the series catalogue: BaseConfig's
 * instructions once per opt-job run kind (outcome and optimal values), and one request for the
 * settings' applied values. Returns nothing when the catalogue has no setting series.
 */
export function buildDatasetInstructions(
  baseConfig: UiConfig,
  catalogue: AppliedSettingSeries[],
  runs: OptJobRun[],
  overrideAlias: UiConfigAliases,
  priorityLevels: number[],
  systemId: number
): Partial<DatasetInstruction>[] {
  const settings = catalogue.filter(isSettingSeries)
  if (!settings.length) {
    return []
  }

  const baseInstructions = firstRunOfEachKind(runs).flatMap((run) => toRunInstructions(baseConfig, run, overrideAlias))

  return [...baseInstructions, toPropertyValuesInstruction(settings, priorityLevels, systemId)]
}

type NarrowedRequest = { instructions: Partial<DatasetInstruction>[]; onlyReturnIds: string[] }

type SourceContract = { sources: { return_id: string }[] }

function hasSources(contract: unknown): contract is SourceContract {
  return typeof contract === 'object' && contract !== null && Array.isArray((contract as SourceContract).sources)
}

function isCalc(instruction: Partial<DatasetInstruction>): instruction is Partial<CalcDatasetInstruction> {
  return instruction.type === 'calc'
}

function getCalcVariables({ contract }: Partial<CalcDatasetInstruction>): string[] {
  const calcs = [contract, ...(contract?.steps ?? [])]

  return calcs.flatMap((calc) => calc?.variables ?? []).filter((variable) => typeof variable === 'string')
}

/**
 * The return ids a request must still produce for `returnIds` to resolve: the ids themselves and,
 * transitively, every variable of a calc producing one of them. Null when a reached calc reads a wildcard
 * variable, whose inputs only resolve against everything fetched.
 */
function closeOverCalcInputs(instructions: Partial<DatasetInstruction>[], returnIds: string[]): Set<string> | null {
  const variablesByCalc = new Map(
    instructions.filter(isCalc).map((calc) => [calc.return_id, getCalcVariables(calc)] as const)
  )
  const requested = new Set<string>()
  const pending = [...returnIds]
  for (let returnId = pending.pop(); returnId !== undefined; returnId = pending.pop()) {
    if (returnId.includes('*')) {
      return null
    }
    if (!requested.has(returnId)) {
      requested.add(returnId)
      pending.push(...(variablesByCalc.get(returnId) ?? []))
    }
  }

  return requested
}

/**
 * One instruction narrowed to the requested return ids, or nothing when it produces none of them. DbS
 * instructions are left to the transport, which narrows them through `onlyReturnIds`.
 */
function narrowInstruction(
  instruction: Partial<DatasetInstruction>,
  requested: Set<string>
): Partial<DatasetInstruction>[] {
  if (instruction.type === 'dbs') {
    return [instruction]
  }
  if (!isCalc(instruction) && hasSources(instruction.contract)) {
    const sources = instruction.contract.sources.filter((source) => requested.has(source.return_id))

    return sources.length
      ? [{ ...instruction, contract: { ...instruction.contract, sources } } as Partial<DatasetInstruction>]
      : []
  }

  return instruction.return_id !== undefined && requested.has(instruction.return_id) ? [instruction] : []
}

/**
 * Narrows built dataset instructions to what the given series need, so a request carries only the user's
 * selection. Calc inputs are kept with the calcs that read them; `onlyReturnIds` narrows DbS instructions
 * in the transport.
 */
export function narrowToSeries(
  instructions: Partial<DatasetInstruction>[],
  series: AppliedSettingSeries[]
): NarrowedRequest {
  const requested = closeOverCalcInputs(
    instructions,
    series.map(({ returnId }) => returnId)
  )
  if (!requested) {
    return { instructions, onlyReturnIds: [] }
  }

  return {
    instructions: instructions.flatMap((instruction) => narrowInstruction(instruction, requested)),
    onlyReturnIds: [...requested],
  }
}
